<template>
  <div class="planner-logs-report">
    <div class="no-print control-bar">
      <div class="d-flex align-items-center flex-wrap">
        <h4 class="mb-0 mr-2">Planner Logs Report</h4>
        <b-badge v-show="!loading && !error" variant="light-info">
          {{ totalLogCount }} record(s)
          <span v-if="groupedByCampus && campusSections.length > 1">
            · {{ campusSections.length }} campuses
          </span>
        </b-badge>
      </div>
      <div class="action-buttons">
        <button
          class="btn-modern btn-print"
          :disabled="loading || !!error"
          @click="print"
        >
          <feather-icon icon="PrinterIcon" size="18" class="mr-50" />
          <span>Print Report</span>
        </button>
        <button class="btn-modern btn-close" @click="close">
          <feather-icon icon="XIcon" size="18" class="mr-50" />
          <span>Close</span>
        </button>
      </div>
    </div>

    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" style="width: 3rem; height: 3rem" />
      <p class="mt-3 font-weight-bold">{{ loadingText }}</p>
    </div>

    <div v-else-if="error" class="report-page report-page--error">
      <div class="error-panel text-center py-5 px-3">
        <feather-icon icon="AlertCircleIcon" size="52" class="text-danger mb-3" />
        <h4 class="text-danger font-weight-bold mb-2">{{ errorTitle }}</h4>
        <p class="text-muted mb-3 error-detail">{{ error }}</p>
        <b-button variant="primary" @click="loadReportData">
          <feather-icon icon="RefreshCwIcon" size="16" class="mr-50" />
          Retry
        </b-button>
      </div>
    </div>

    <div v-else class="report-page">
      <div class="report-header-modern mb-4">
        <div class="d-flex align-items-center">
          <div class="logo-wrapper mr-3" v-if="schoolLogo">
            <img :src="schoolLogo" alt="Logo" class="school-logo-img" />
          </div>
          <div class="header-text-block">
            <h2 class="school-name-full mb-0">
              {{ campusDisplayName }}
            </h2>
            <div class="report-info-line mt-1">
              <span class="report-name-text">Planner Logs Report</span>
              <span v-if="showDateRange" class="date-range-text ml-2">
                <strong>{{ formatHeaderDate(dtFrom) }}</strong>
                to
                <strong>{{ formatHeaderDate(dtTo) }}</strong>
              </span>
            </div>
            <div class="report-sub-meta mt-1">
              <span><strong>Teacher:</strong> {{ teacherLabel }}</span>
              <span class="mx-1">·</span>
              <span><strong>Generated:</strong> {{ currentDateTime }}</span>
            </div>
          </div>
        </div>
      </div>

      <div v-if="totalLogCount === 0" class="empty-state text-center py-5">
        <feather-icon icon="InboxIcon" size="40" class="text-muted mb-2" />
        <h5 class="text-muted font-weight-bold mb-1">No records found</h5>
        <p class="text-muted mb-0">
          {{
            showDateRange
              ? "No planner logs match the selected campus, teacher, and date range."
              : "No planner logs match the selected campus and teacher."
          }}
        </p>
      </div>

      <div v-else>
        <section
          v-for="section in campusSections"
          :key="section.key"
          class="campus-section"
          :class="{ 'campus-section--grouped': groupedByCampus && section.title }"
        >
          <div
            v-if="groupedByCampus && section.title"
            class="campus-section-header"
          >
            <h3 class="campus-section-title mb-0">{{ section.title }}</h3>
            <span class="campus-section-count">{{ section.count }} record(s)</span>
          </div>

          <div v-if="!section.logs.length" class="campus-empty text-muted py-3">
            No planner logs for this campus.
          </div>

          <div v-else class="planner-table-wrap">
            <table class="planner-logs-table">
              <thead>
                <tr class="main-header">
                  <th class="col-sno text-center">S#</th>
                  <th class="col-date">Planner Date</th>
                  <th class="col-date">Completion Date</th>
                  <th v-if="showTeacherColumn" class="col-teacher">Teacher</th>
                  <th class="col-class">Class</th>
                  <th class="col-subject">Subject</th>
                  <th class="col-topic">Topic / Sub Topic</th>
                  <th class="col-status text-center">Status</th>
                  <th class="col-notes">Notes</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(log, index) in section.logs"
                  :key="`${section.key}-${log._key || index}`"
                >
                  <td class="text-center">{{ index + 1 }}</td>
                  <td class="date-cell">
                    <div>{{ formatDate(log.dateFrom) }}</div>
                    <div class="date-sep">to</div>
                    <div>{{ formatDate(log.dateTo) }}</div>
                  </td>
                  <td class="date-cell">
                    {{ formatDateTime(log.completedDate) }}
                  </td>
                  <td v-if="showTeacherColumn" class="teacher-cell">
                    {{ log.teacherName }}
                  </td>
                  <td>{{ log.className }}</td>
                  <td>{{ log.subjectName }}</td>
                  <td class="topic-cell">
                    <div class="topic-main">{{ log.mainTopic }}</div>
                    <div v-if="log.subTopic" class="topic-sub">
                      {{ log.subTopic }}
                    </div>
                  </td>
                  <td class="text-center">
                    <span class="status-pill" :class="statusClass(log.status)">
                      {{ log.status }}
                    </span>
                  </td>
                  <td class="notes-cell">{{ log.notes }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      </div>

      <div class="page-footer">
        <small>
          Powered by MySkool.app | Printed by
          {{ $store.state.userData.name || "—" }} · {{ currentDateTime }}
        </small>
      </div>
    </div>
  </div>
</template>

<script>
import { BButton, BSpinner, BBadge } from "bootstrap-vue";
import {
  fetchPlannerLogs,
  buildCampusSections,
  describePlannerLogsRequestError,
} from "@/utils/plannerLogsReport";

export default {
  name: "PlannerLogsPrint",
  components: {
    BButton,
    BSpinner,
    BBadge,
  },
  data() {
    return {
      loading: true,
      loadingText: "Loading planner logs...",
      error: null,
      errorTitle: "Could not load report",
      campusSections: [],
      groupedByCampus: false,
      staffName: "",
      campusName: "",
      schoolLogo: null,
      db: "",
      campusID: "",
      tID: 0,
      dtFrom: "",
      dtTo: "",
      showDateRange: false,
    };
  },
  computed: {
    campusDisplayName() {
      const isAllCampus =
        Number(this.campusID) === 0 ||
        !this.campusName ||
        this.campusName === "All";
      if (this.groupedByCampus && isAllCampus) {
        const branch = this.$store.state.currentBranch;
        const ho = (branch && branch.branch) || "Head Office";
        return `${ho} — All Campuses`;
      }
      if (this.campusName && this.campusName !== "All") {
        return this.campusName;
      }
      const branch = this.$store.state.currentBranch;
      return (branch && branch.branch) || "My Skool";
    },
    totalLogCount() {
      return (this.campusSections || []).reduce(
        (sum, sec) => sum + (sec.logs ? sec.logs.length : 0),
        0
      );
    },
    flatLogsForMeta() {
      return (this.campusSections || []).flatMap((s) => s.logs || []);
    },
    teacherLabel() {
      if (this.staffName) return this.staffName;
      return Number(this.tID) > 0 ? "Selected teacher" : "All Teachers";
    },
    showTeacherColumn() {
      return (
        !this.staffName ||
        this.staffName === "All Teachers" ||
        Number(this.tID) === 0
      );
    },
    currentDateTime() {
      const now = new Date();
      return now.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
  },
  created() {
    this.initReport();
  },
  methods: {
    initReport() {
      const q = this.$route.query;
      this.db = q.db || this.$store.state.userData.db;
      this.campusID =
        q.campusID != null && q.campusID !== ""
          ? q.campusID
          : this.$store.state.userData.cId;
      this.campusName = q.campusName || "";
      this.tID = Number(q.tID) || 0;
      this.staffName = q.staffName || "";
      this.showDateRange =
        q.showDateRange === "1" ||
        q.showDateRange === 1 ||
        q.showDateRange === true;

      this.dtFrom = q.dtFrom || "";
      this.dtTo = q.dtTo || "";

      if (!this.dtFrom) {
        this.dtFrom = "1990-01-01";
      }
      if (!this.dtTo) {
        const now = new Date();
        const yyyy = now.getFullYear();
        const mm = String(now.getMonth() + 1).padStart(2, "0");
        const dd = String(now.getDate()).padStart(2, "0");
        this.dtTo = `${yyyy}-${mm}-${dd}`;
      }

      this.schoolLogo =
        (this.$store.state.currentBranch &&
          this.$store.state.currentBranch.logo) ||
        null;

      if (!this.db || this.campusID === "") {
        this.loading = false;
        this.errorTitle = "Invalid report link";
        this.error =
          "Missing database or campus. Open this report again from Reports → Planner Logs.";
        return;
      }

      this.loadReportData();
    },

    async loadReportData() {
      try {
        this.loading = true;
        this.error = null;
        this.errorTitle = "Could not load report";

        const parsed = await fetchPlannerLogs({
          domain: this.$store.state.domain,
          token: this.$store.state.userData.token,
          db: this.db,
          campusID: this.campusID,
          tID: this.tID,
          dtFrom: this.dtFrom,
          dtTo: this.dtTo,
        });

        if (!parsed.ok) {
          this.campusSections = [];
          this.groupedByCampus = false;
          this.errorTitle = "Report unavailable";
          this.error =
            parsed.message ||
            "The server did not return planner logs for these filters.";
          return;
        }

        this.groupedByCampus = !!parsed.groupedByCampus;
        this.campusSections = buildCampusSections(parsed);

        const flat = this.flatLogsForMeta;
        if (!this.staffName && this.tID > 0 && flat.length > 0) {
          this.staffName = flat[0].teacherName;
        }
        if (this.tID === 0 && !this.staffName) {
          this.staffName = "All Teachers";
        }
      } catch (err) {
        console.error("Failed to load planner logs:", err);
        this.campusSections = [];
        this.groupedByCampus = false;
        this.errorTitle = "Connection error";
        this.error = describePlannerLogsRequestError(err);
      } finally {
        this.loading = false;
      }
    },

    formatHeaderDate(dateStr) {
      if (!dateStr) return "—";
      const d = new Date(dateStr);
      if (Number.isNaN(d.getTime())) return dateStr;
      return d.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    },

    formatDate(dateStr) {
      if (!dateStr) return "—";
      const date = new Date(dateStr);
      if (Number.isNaN(date.getTime())) return String(dateStr);
      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    },

    formatDateTime(dateStr) {
      if (!dateStr) return "—";
      const date = new Date(dateStr);
      if (Number.isNaN(date.getTime())) return String(dateStr);
      return (
        date.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        }) +
        " " +
        date.toLocaleTimeString("en-GB", {
          hour: "2-digit",
          minute: "2-digit",
        })
      );
    },

    statusClass(status) {
      const s = String(status || "").toLowerCase();
      if (s.includes("pending")) return "status-pill--pending";
      if (s.includes("late")) return "status-pill--late";
      if (s.includes("complete") || s.includes("time")) {
        return "status-pill--ok";
      }
      return "status-pill--neutral";
    },

    print() {
      window.print();
    },

    close() {
      window.close();
    },
  },
};
</script>

<style lang="scss" scoped>
.planner-logs-report {
  background: #f0f2f5;
  min-height: 100vh;
  padding: 2rem;
  font-family: "Outfit", "Inter", sans-serif;
  color: #2d3436;
}

.control-bar {
  background: rgba(255, 255, 255, 0.95);
  padding: 1.25rem 2rem;
  margin-bottom: 1.5rem;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
  max-width: 1400px;
  margin-left: auto;
  margin-right: auto;

  h4 {
    font-weight: 700;
    color: #444;
  }
}

.action-buttons {
  display: flex;
  gap: 12px;
}

.btn-modern {
  display: inline-flex;
  align-items: center;
  padding: 10px 22px;
  border-radius: 10px;
  font-weight: 700;
  font-size: 0.95rem;
  border: none;
  cursor: pointer;
}

.btn-print {
  background: linear-gradient(135deg, #6c5ce7 0%, #a29bfe 100%);
  color: #fff;
}

.btn-close {
  background: #fff;
  color: #636e72;
  border: 1px solid #dfe6e9;
}

.report-page {
  background: #fff;
  padding: 2.5rem 2rem;
  border-radius: 12px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.08);
  max-width: 1400px;
  margin: 0 auto;
}

.report-header-modern {
  border-bottom: 2px solid #333;
  padding-bottom: 1rem;

  .school-logo-img {
    height: 72px;
    max-width: 110px;
    object-fit: contain;
  }

  .school-name-full {
    font-size: 1.5rem;
    font-weight: 800;
    text-transform: uppercase;
    color: #000;
  }

  .report-info-line {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem;
    font-size: 1rem;

    .report-name-text {
      font-weight: 700;
      padding-right: 12px;
      border-right: 2px solid #ddd;
    }
  }

  .report-sub-meta {
    font-size: 0.9rem;
    color: #555;
  }
}

.campus-section {
  margin-top: 1rem;

  &--grouped {
    margin-top: 1.75rem;
    padding-top: 0.25rem;
    border-top: 1px solid #e9ecef;

    &:first-of-type {
      margin-top: 1rem;
      border-top: none;
      padding-top: 0;
    }
  }
}

.campus-section-header {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.5rem 1rem;
  margin-bottom: 0.75rem;
  padding: 0.65rem 1rem;
  background: linear-gradient(90deg, #eef2ff 0%, #f8f9fa 100%);
  border-left: 4px solid #6c5ce7;
  border-radius: 0 8px 8px 0;
}

.campus-section-title {
  font-size: 1.05rem;
  font-weight: 800;
  color: #2d3436;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.campus-section-count {
  font-size: 0.85rem;
  font-weight: 600;
  color: #636e72;
}

.planner-table-wrap {
  overflow-x: auto;
  margin-top: 0.25rem;
}

.planner-logs-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.88rem;

  .main-header th {
    background: #2c3e50;
    color: #fff;
    padding: 10px 8px;
    font-weight: 700;
    text-transform: uppercase;
    font-size: 0.72rem;
    letter-spacing: 0.03em;
    border: 1px solid #1a252f;
  }

  tbody td {
    padding: 8px 10px;
    border: 1px solid #dee2e6;
    vertical-align: top;
    line-height: 1.4;
  }

  tbody tr:nth-child(even) {
    background: #fafbfc;
  }
}

.date-cell {
  font-size: 0.82rem;
  white-space: nowrap;

  .date-sep {
    font-size: 0.75rem;
    color: #888;
    font-style: italic;
    margin: 2px 0;
  }
}

.topic-cell {
  .topic-main {
    font-weight: 600;
  }

  .topic-sub {
    margin-top: 4px;
    padding-top: 4px;
    border-top: 1px dashed #ccc;
    color: #444;
    font-size: 0.85rem;
  }
}

.notes-cell {
  max-width: 220px;
  word-break: break-word;
}

.status-pill {
  display: inline-block;
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;

  &--pending {
    background: #fff3cd;
    color: #856404;
  }

  &--late {
    background: #fee2e2;
    color: #b91c1c;
  }

  &--ok {
    background: #dcfce7;
    color: #15803d;
  }

  &--neutral {
    background: #f1f2f6;
    color: #444;
  }
}

.page-footer {
  text-align: center;
  margin-top: 1.5rem;
  padding-top: 0.75rem;
  border-top: 1px solid #eee;
  color: #666;
}

.error-detail {
  max-width: 520px;
  margin-left: auto;
  margin-right: auto;
}

@media print {
  .no-print {
    display: none !important;
  }

  .planner-logs-report {
    background: #fff !important;
    padding: 0 !important;
    min-height: auto !important;
  }

  .report-page {
    box-shadow: none !important;
    padding: 0.75cm 1cm !important;
    max-width: 100% !important;
  }

  .planner-logs-table .main-header th {
    background: #eef1f5 !important;
    color: #000 !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .page-footer {
    display: none;
  }
}
</style>

<style lang="scss">
@page {
  size: A4 landscape;
  margin: 10mm;
}

@media print {
  html,
  body {
    width: 100% !important;
    height: auto !important;
    min-height: 0 !important;
    margin: 0 !important;
    padding: 0 !important;
    background: #fff !important;
    overflow: visible !important;
  }

  #app,
  .content-wrapper,
  .content-area-wrapper,
  .layout-full,
  .router-view {
    min-height: 0 !important;
    height: auto !important;
    max-width: 100% !important;
    width: 100% !important;
    margin: 0 !important;
    padding: 0 !important;
    overflow: visible !important;
  }
}
</style>
